/**
 * One session and one fetch wrapper for the whole console.
 *
 * **The defect this replaces.** Each of the seven static admin pages carried its own login form and its own
 * `let token = null; // deliberately not persisted`. Moving from Users to Cranes meant signing in again — six
 * separate authentications to do one round of administration — and none of the six pages linked back to the
 * index, so there was no navigation either. That is most of why the surface read as a utility rather than a
 * platform.
 *
 * **Why sessionStorage rather than memory or localStorage.** In-memory was the old behaviour and is what broke
 * navigation. localStorage survives browser restart, which for a shared plant PC means the next person to open
 * the browser inherits an ADMIN session — the opposite of what the original comment was protecting against.
 * sessionStorage is scoped to the tab: it survives an in-app route change and a refresh, and dies when the tab
 * closes. Refresh tokens are handled the same way.
 *
 * The access token is short-lived (30 min by the backend's own config) and the refresh below is single-flight,
 * so a dashboard firing nine parallel metric requests on a stale token produces one refresh, not nine.
 */

const ACCESS = 'ems.access'
const REFRESH = 'ems.refresh'
const ROLE = 'ems.role'
const NAME = 'ems.name'
const STAFF = 'ems.staff'

const BASE = '/api/v1'

export const session = {
  get token() { return sessionStorage.getItem(ACCESS) },
  get refreshToken() { return sessionStorage.getItem(REFRESH) },
  get role() { return sessionStorage.getItem(ROLE) },
  get name() { return sessionStorage.getItem(NAME) },
  get staff() { return sessionStorage.getItem(STAFF) },
  get signedIn() { return !!sessionStorage.getItem(ACCESS) },

  save({ accessToken, refreshToken, role, name, staffNumber }) {
    sessionStorage.setItem(ACCESS, accessToken)
    if (refreshToken) sessionStorage.setItem(REFRESH, refreshToken)
    if (role) sessionStorage.setItem(ROLE, role)
    if (name) sessionStorage.setItem(NAME, name)
    if (staffNumber) sessionStorage.setItem(STAFF, staffNumber)
  },

  clear() {
    [ACCESS, REFRESH, ROLE, NAME, STAFF].forEach((k) => sessionStorage.removeItem(k))
  },
}

/** Decode the JWT payload for role/name without a library. Never trusted for authorization — the server
 *  re-checks every request — only used to decide which nav items to draw. */
function claims(token) {
  try {
    const part = token.split('.')[1]
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/').padEnd(part.length + ((4 - (part.length % 4)) % 4), '='))
    return JSON.parse(json)
  } catch {
    return {}
  }
}

export async function login(staffNumber, password) {
  const res = await fetch(`${BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ staffNumber, password }),
  })
  if (!res.ok) {
    // 401 means the credentials are wrong; anything else is the server or the network, and saying
    // "check your credentials" for a 503 sends someone to reset a password that was never the problem.
    throw new ApiError(res.status, res.status === 401
      ? 'Staff number or password is incorrect.'
      : 'Could not reach the server. Try again shortly.')
  }
  const body = await res.json()
  // The server returns role and name on TokenResponse, so those are read from the body; the JWT decode is
  // only a fallback for the refresh path, where the response shape is the same but a claim may be newer.
  const c = claims(body.accessToken)
  session.save({
    accessToken: body.accessToken,
    refreshToken: body.refreshToken,
    role: body.role || c.role,
    name: body.name,
    staffNumber: c.staff || staffNumber,
  })
  return session.role
}

export async function logout() {
  /*
   * Local-only sign-out, because **there is no server-side logout endpoint**: `/api/v1/auth` exposes
   * `/login` and `/refresh` and nothing else. Clearing sessionStorage drops both tokens, so this tab cannot
   * make another authenticated call.
   *
   * What it does not do is revoke the refresh token server-side, which means a token copied out of storage
   * before sign-out stays valid until it expires. Recorded rather than papered over: the honest fix is a
   * `POST /auth/logout` that invalidates the refresh token, and inventing a call to an endpoint that does
   * not exist would have produced a silent 404 and the appearance of revocation.
   */
  session.clear()
}

export class ApiError extends Error {
  constructor(status, message, body) {
    super(message)
    this.status = status
    this.body = body
  }
}

/** Single-flight refresh: concurrent 401s share one attempt. */
let refreshing = null

async function refreshOnce() {
  if (!session.refreshToken) return false
  if (!refreshing) {
    refreshing = (async () => {
      try {
        const res = await fetch(`${BASE}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: session.refreshToken }),
        })
        if (!res.ok) return false
        const body = await res.json()
        const c = claims(body.accessToken)
        session.save({ accessToken: body.accessToken, refreshToken: body.refreshToken, role: c.role })
        return true
      } catch {
        return false
      } finally {
        // Cleared on the next tick so callers awaiting this promise all see the same result first.
        setTimeout(() => { refreshing = null }, 0)
      }
    })()
  }
  return refreshing
}

/**
 * Authenticated fetch with one transparent refresh on 401.
 *
 * Errors carry the server's own message where there is one. The M9 audit found a single "check your
 * connection" message shown for a 403, which sent a supervisor to restart their phone over a permission they
 * did not have — so the status is preserved and callers distinguish it.
 */
export async function api(path, { method = 'GET', body, signal, raw = false } = {}) {
  const send = () => fetch(`${BASE}${path}`, {
    method,
    signal,
    headers: {
      ...(body ? { 'Content-Type': 'application/json' } : {}),
      ...(session.token ? { Authorization: `Bearer ${session.token}` } : {}),
    },
    ...(body ? { body: typeof body === 'string' ? body : JSON.stringify(body) } : {}),
  })

  let res = await send()
  if (res.status === 401 && session.refreshToken) {
    if (await refreshOnce()) res = await send()
  }

  if (res.status === 401) {
    session.clear()
    throw new ApiError(401, 'Your session has expired. Sign in again.')
  }
  if (res.status === 403) {
    throw new ApiError(403, 'Your role does not permit this. Ask an administrator.')
  }
  if (!res.ok) {
    let detail
    try { detail = await res.json() } catch { /* not JSON */ }
    throw new ApiError(res.status, detail?.message || `Request failed (${res.status}).`, detail)
  }

  if (raw) return res
  if (res.status === 204) return null
  return res.json()
}

/** Query-string helper that omits null/undefined/'' rather than sending empty params the server must ignore. */
export function qs(params) {
  const p = new URLSearchParams()
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v !== null && v !== undefined && v !== '') p.set(k, v)
  })
  const s = p.toString()
  return s ? `?${s}` : ''
}
